import type React from "react";

import type { FeelingAtHomeWidgetV2Dto } from "@/api/types";
import { InfoCircleIcon } from "@/components/Icons/InfoCircleIcon";
import { Speedometer } from "@/components/Speedometer/Speedometer";
import { Button } from "@/design-system/Button";
import { canListAnySurvey } from "@/helpers/permissions";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { FahWidgetScoreReferenceBadge } from "./FahWidgetScoreReferenceBadge";

type FahWidgetSpeedometerProps = {
  data: FeelingAtHomeWidgetV2Dto;
};

export const FahWidgetSpeedometer = ({ data }: FahWidgetSpeedometerProps): React.ReactNode => {
  const { t } = useTranslation();
  const slug = useSlug();
  const hasPermission = usePermission();

  const getDetailsLink = () => {
    let urlFahDetails: string = routes.surveys.fah({ slug });

    // Keep the direct survey fallback for projects without automatic details.
    if (!data.hasFeelAtHomeDetailsLink && data.manualSurveyId) {
      urlFahDetails = routes.surveys.details({ slug, id: data.manualSurveyId });
    }

    return urlFahDetails;
  };

  const score = data.totalFeelingAtHomeScore ? data.totalFeelingAtHomeScore.rating / 10 : 0;
  const label = t("component.analytics-widget.common.aop-benchmark.label", {
    score: data.benchmarkFeelingAtHomeScore?.rating?.toFixed(1) ?? "-",
  });

  return (
    <section className="flex w-full flex-col items-center justify-self-center pt-space-md @xl:pt-space-lg">
      <h2 className="text-center text-heading-3">{t("component.analytics-widget.fah.speedometer.title")}</h2>
      <section className="flex w-full flex-col items-center gap-space-xl p-space-md @xl:p-space-lg">
        <section className="flex flex-col items-center gap-space-md @md:w-full @md:max-w-measure-default">
          <Speedometer
            score={score}
            renderCurrentValue={(currScore) => (currScore ? (currScore * 10).toFixed(1) : undefined)}
          />
          {hasPermission(canListAnySurvey) && (
            <Button type="link" icon={<InfoCircleIcon />} variant="ghostPrimary" href={getDetailsLink()}>
              {t("component.analytics-widget.fah.btn.view-details")}
            </Button>
          )}

          <p className="text-center text-body">
            {label}
            <br />
            <span className="text-text-secondary">{t("component.analytics-widget.fah.speedometer.description")}</span>
          </p>
        </section>
        <footer className="flex w-full justify-center">
          <FahWidgetScoreReferenceBadge
            currValue={data.totalFeelingAtHomeScore?.rating ?? 0}
            referenceValue={data.lastFeelingAtHomeScore?.rating ?? 0}
          />
        </footer>
      </section>
    </section>
  );
};
